import { useTranslation } from 'react-i18next';
import { SendReceiveButtons } from './SendReceiveButtons';
import { CountUp } from './CountUp';
import { TodayChange } from './TodayChange';
import { EyeIcon, EyeOffIcon } from './icons';
import { useTelegramUser, telegramName } from '../../hooks/useTelegramUser';
import { useMe } from '../../hooks/useMe';
import { useTotalBalance } from '../../hooks/useTotalBalance';
import { HIDDEN_BALANCE, useUiStore } from '../../store/ui';

type Props = {
  onReceive?: () => void;
  onSend?: () => void;
  onSwap?: () => void;
};

export function WalletHeader({ onReceive, onSend, onSwap }: Props) {
  const { t } = useTranslation();
  const user = useTelegramUser();
  const { me } = useMe();
  const name =
    telegramName(user, '') ||
    me?.nickname ||
    me?.email ||
    t('wallet.username');
  const avatarUrl = user?.photo_url || me?.avatarUrl || null;
  // Headline "total" = wallet + cards + staking + INCPT.
  const { totalUsd } = useTotalBalance();
  const balanceHidden = useUiStore((s) => s.balanceHidden);
  const toggleBalanceHidden = useUiStore((s) => s.toggleBalanceHidden);

  return (
    <header className="flex flex-col items-center gap-7 px-4 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
      {/* Profile */}
      <div className="flex flex-col items-center gap-[10px]">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-11 w-11 rounded-full object-cover shadow-sm" />
        ) : (
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[15px] font-medium text-ink shadow-sm">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-[14px] text-ink">{name}</span>
      </div>

      {/* Balance. The row is full width so the amount's center is the screen's
          center; the eye is pulled out of that box and hangs off its right edge. */}
      <div className="-mt-4 flex w-full flex-col items-center gap-[14px] text-center">
        <span className="text-[14px] text-muted">{t('wallet.total')}</span>
        <div className="flex w-full justify-center">
          <div className="relative">
            {balanceHidden ? (
              <span className="inline-block text-[44px] font-bold leading-none tabular-nums text-ink">
                {HIDDEN_BALANCE}
              </span>
            ) : (
              <CountUp
                value={totalUsd}
                className="inline-block text-[44px] font-bold leading-none tabular-nums text-ink"
              />
            )}
            <button
              type="button"
              onClick={toggleBalanceHidden}
              aria-label={balanceHidden ? t('wallet.showBalance') : t('wallet.hideBalance')}
              className="absolute left-full top-1/2 ml-1 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted active:bg-black/[0.05]"
            >
              {balanceHidden ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
            </button>
          </div>
        </div>
        <TodayChange />
      </div>

      {/* Receive / Send / Swap */}
      <SendReceiveButtons onReceive={onReceive} onSend={onSend} onSwap={onSwap} />
    </header>
  );
}
